// Вътрешността на попъпа на `PeriodPicker`: седемте пресета вляво (на телефон —
// хоризонтален ред над календара) и календарът вдясно. Всеки избор се прилага
// веднага: пресетът — с натискането си, календарът — с ВТОРИЯ клик (краят на
// диапазона; същият ден два пъти = един ден, по-ранен ден сменя началото).
// Докато е избрано само началото, редът отдолу го показва и казва какво следва.
//
// Клавиатура: при отваряне фокусът е на текущия пресет (при произволен период —
// на първия); ↑/↓ (и ←/→ в реда на телефона, Home/End) местят по пресетите,
// Enter/Space прилагат; Tab → месеца и дните на календара (стрелки по дните,
// Enter/Space избира — `RangeCalendar`). Esc отвсякъде затваря без промяна —
// с preventDefault, иначе минава и през глобалната Esc навигация.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useInventoryShell } from "../shell/InventoryShellContext";
import { KbdChip } from "../KbdChip";
import { RangeCalendar } from "./RangeCalendar";
import {
  PERIOD_PRESET_KEYS,
  formatPeriod,
  periodOfPreset,
  presetOfPeriod,
  type Period,
} from "./periodPresets";

type Props = {
  value: Period;
  onApply: (period: Period) => void;
  onClose: (returnFocus: boolean) => void;
};

const PREVIOUS_KEYS = ["ArrowUp", "ArrowLeft"];
const NEXT_KEYS = ["ArrowDown", "ArrowRight"];

export const PeriodPickerPanel = ({ value, onApply, onClose }: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Началото на нов диапазон след първия клик; null — календарът показва периода
  const [pendingFrom, setPendingFrom] = useState<Dayjs | null>(null);
  const activePreset = presetOfPeriod(value);
  // Единственият пресет в Tab реда (останалите — със стрелките)
  const tabPreset = activePreset ?? PERIOD_PRESET_KEYS[0];

  // При отваряне фокусът отива на пресета. Порталът на antd закача попъпа към
  // документа СЛЕД ефекта на съдържанието — опитът се повтаря, докато попъпът
  // стане част от страницата (както в `ColumnFilterPanel`); preventScroll —
  // попъпът още се позиционира.
  useEffect(() => {
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const focusPreset = () => {
      const root = rootRef.current;
      if (!root) return;
      if (!root.isConnected) {
        if (attempts++ < 20) timer = setTimeout(focusPreset, 16);
        return;
      }
      if (root.contains(document.activeElement)) return;
      root.querySelector<HTMLElement>('[data-preset][tabindex="0"]')?.focus({ preventScroll: true });
    };
    focusPreset();
    return () => clearTimeout(timer);
  }, []);

  const onCalendarChange = (from: Dayjs | null, to: Dayjs | null) => {
    if (from && to) onApply({ from: from.startOf("day"), to: to.startOf("day") });
    else setPendingFrom(from);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose(true);
      return;
    }
    const target = event.target as HTMLElement;
    if (!target.hasAttribute("data-preset")) return;
    const presets = Array.from(rootRef.current?.querySelectorAll<HTMLElement>("[data-preset]") ?? []);
    const index = presets.indexOf(target);
    let next: HTMLElement | undefined;
    if (event.key === "Home") next = presets[0];
    else if (event.key === "End") next = presets[presets.length - 1];
    else if (PREVIOUS_KEYS.includes(event.key)) next = presets[index - 1];
    else if (NEXT_KEYS.includes(event.key)) next = presets[index + 1];
    else return;
    event.preventDefault();
    next?.focus();
  };

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={t("inventoryPage.periodCustom")}
      onKeyDown={onKeyDown}
      className={`flex overflow-hidden ${isNarrow ? "w-[min(92vw,360px)] flex-col" : "w-[560px]"}`}
    >
      <div
        role="group"
        className={`flex shrink-0 gap-0.5 overflow-x-auto bg-fill p-2.5 ${
          isNarrow ? "flex-row border-b border-shade-2" : "w-[170px] flex-col border-r border-shade-2"
        }`}
      >
        {PERIOD_PRESET_KEYS.map((key) => {
          const active = activePreset === key;
          return (
            <button
              key={key}
              type="button"
              data-preset={key}
              tabIndex={key === tabPreset ? 0 : -1}
              aria-pressed={active}
              onClick={() => onApply(periodOfPreset(key))}
              className={`cursor-pointer whitespace-nowrap rounded-[7px] border-none px-2.5 py-2 text-left text-[13px] leading-[normal] outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] ${
                active
                  ? "bg-brand-tint font-semibold text-brand-deep-ink"
                  : "bg-transparent text-ink hover:bg-shade-1"
              }`}
            >
              {t(`inventoryPage.period_${key}`)}
            </button>
          );
        })}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2.5 px-3.5 py-3">
        <RangeCalendar
          from={pendingFrom ?? value.from}
          to={pendingFrom ? null : value.to}
          maxDate={dayjs()}
          onChange={onCalendarChange}
        />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-shade-2 pt-2 text-[12px] leading-[normal] text-muted">
          <span aria-live="polite" className={pendingFrom ? "font-medium text-ink" : ""}>
            {pendingFrom
              ? `${pendingFrom.format("DD.MM.YYYY")} – … · ${t("inventoryPage.tbPeriodPickEnd")}`
              : `${formatPeriod(value)} · ${t("inventoryPage.tbPeriodPickRange")}`}
          </span>
          {!isNarrow && (
            <span className="ml-auto whitespace-nowrap">
              <KbdChip>Esc</KbdChip> {t("inventoryPage.catKeyEscClose")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeriodPickerPanel;
